/* Wizard · Step 5 — Lab Instructions
 * Extracted iter101.91 from `/app/frontend/src/pages/doctor/CaseSubmissionPage.jsx`.
 *
 * Contains three concerns pulled out of the ~9,000-line wizard file so
 * this step can be edited independently:
 *   · SelfPlanLabInstructions — the big accordion stack (used by BOTH
 *     Self-Plan and CoE, though CoE hides some sub-blocks).
 *   · StepLabInstructions     — thin wrapper that renders either the
 *     accordion stack (Self-Plan) or a shorter CoE-mode message.
 *   · TierInclusionRow        — small row helper used inside Step 5 UI.
 */
import React, { useState, useEffect, useMemo, useRef } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "sonner";
import {
    ArrowSquareDown, ArrowSquareUp, Calculator, ChartBar, Check, Circle,
    CircleHalf, Cube, Hourglass, Info, ListBullets, Notepad, Package,
    Pencil, Printer, Prohibit, Ruler, Sliders, Tooth,
    User as UserIcon, Warning, X,
} from "@phosphor-icons/react";
import {
    LabSection, LabChip, LabChoiceCard, LabInfoNote, LabGlanceRow,
    FdiChipPicker, PhaseSplitVisualizer,
} from "@/components/case/wizard/lab-section";


export function SelfPlanLabInstructions({ rx, setRx, planTimeline, setPlanTimeline, clinical, setClinical, tiers = [], planningMode = "doctor" }) {
    const upd = (k, v) => setRx((p) => ({ ...p, [k]: v }));
    const updPlan = (k, v) => setPlanTimeline((p) => ({ ...p, [k]: v }));
    const updClinical = (k, v) => setClinical((p) => ({ ...p, [k]: v }));
    /* iter98.99ak — `planningMode` was added so this component now serves
       BOTH Self-Plan and CoE flows. CoE shares the same accordion-based
       Lab Instructions visual rhythm, but planner-authored sub-blocks
       (aligner counts U/L, wear schedule, treatment timeline, retention
       recalls, tier preview/fit hints) stay hidden — those are filled
       by Invisique's planning team after the case is submitted. */
    const isSelfPlan = planningMode === "doctor";

    // ─── Section 1: Arches & aligner counts ───
    const archValue = clinical?.arch || "both"; // "upper" | "lower" | "both"
    const upperCount = planTimeline?.aligner_count_upper ?? "";
    const lowerCount = planTimeline?.aligner_count_lower ?? "";
    const wearDaysRaw = planTimeline?.wear_schedule_default_days;
    const wearDays = wearDaysRaw === "" || wearDaysRaw == null ? null : Number(wearDaysRaw);
    const PRESET_WEAR = [7, 10, 14, 21];
    const wearIsPreset = wearDays != null && PRESET_WEAR.includes(wearDays);
    const wearIsCustom = wearDays != null && !wearIsPreset && wearDays > 0;

    // ─── Section 2: Clinical preferences ───
    const iprDir = rx?.ipr_directive || "per_plan";
    const attDir = rx?.attachments_directive || "per_plan";
    const elaDir = rx?.elastics_directive || "per_plan";

    // ─── Section 3: Patient profile ───
    const flags = Array.isArray(rx?.patient_flags) ? rx.patient_flags : [];
    const hasBrux = flags.includes("bruxism");
    const hasTmj = flags.includes("tmj");
    const hasNone = flags.includes("none");
    const toggleFlag = (key) => {
        if (key === "none") {
            // "None" is exclusive — selecting it clears the others.
            upd("patient_flags", hasNone ? [] : ["none"]);
            return;
        }
        const next = new Set(flags.filter((x) => x !== "none"));
        if (next.has(key)) next.delete(key); else next.add(key);
        upd("patient_flags", Array.from(next));
    };
    const complexity = rx?.case_complexity || "moderate";

    // ─── Section 4: Shipment preference ───
    const shipPref = rx?.shipment_preference || "all_at_once";
    const isPhased = shipPref === "phased";

    /* iter97.76 — Enhancement #1: Smart prefill from medical history.
       When the doctor enters keywords like "bruxism" / "grinding" /
       "tmj" / "temporomandibular" in Step 2 medical history, we auto-
       tick those flags on first load — but only if the doctor hasn't
       already explicitly set patient_flags. Manual changes always
       win. We use a ref so the prefill flag doesn't trigger re-renders. */
    const prefillRef = useRef(false);
    useEffect(() => {
        if (prefillRef.current) return;
        if (!clinical?.medical_history) return;
        prefillRef.current = true;
        if (Array.isArray(rx?.patient_flags) && rx.patient_flags.length > 0) {
            return;
        }
        const text = String(clinical.medical_history).toLowerCase();
        const detected = [];
        if (/brux|grinding|grind teeth|nocturnal grind/.test(text)) detected.push("bruxism");
        if (/\btmj\b|temporomandibular|jaw joint|tmd/.test(text)) detected.push("tmj");
        if (detected.length > 0) {
            setRx((p) => ({ ...p, patient_flags: detected }));
        }
    }, [clinical?.medical_history, rx?.patient_flags, setRx]);

    /* iter97.76 — Enhancement #2: Live ETA pill on Wear schedule.
       Treatment duration is governed by the longer arch since upper +
       lower are worn simultaneously. */
    const maxAligners = Math.max(Number(upperCount) || 0, Number(lowerCount) || 0);
    /* iter98.99dc — Stage count is the canonical input to tier matching
       (1 stage = 1 upper + 1 lower piece). Previously this was U+L which
       inflated against tier ranges defined in stages. */
    const totalStages = maxAligners;
    const physicalAligners = (Number(upperCount) || 0) + (Number(lowerCount) || 0);
    const etaDays = (maxAligners > 0 && wearDays > 0) ? maxAligners * wearDays : null;
    const etaWeeks = etaDays != null ? Math.round(etaDays / 7) : null;
    const etaMonths = etaDays != null ? (etaDays / 30.4).toFixed(1) : null;

    /* iter98.99au / iter98.99dc — Tier-fit guidance based on STAGE count
       (max(U, L)). Mirrors the server-side `autoSelfPlanTier` rule + the
       stage range semantics (aligner_min ≤ stages ≤ aligner_count).
       Three return kinds:
         · 'below-min'    → stages < smallest tier's aligner_min
         · 'exceeds-all'  → stages > largest tier's aligner_count
         · normal         → fits within a tier's range; carries
                            complexity_label + duration window so the
                            doctor sees what that tier means. */
    const tierFit = useMemo(() => {
        if (!tiers || tiers.length === 0 || totalStages === 0) return null;
        const activeTiers = (tiers || []).filter((t) => t.is_active !== false);
        if (activeTiers.length === 0) return null;
        const sortedAsc = activeTiers
            .slice()
            .sort((a, b) => Number(a.aligner_count || 0) - Number(b.aligner_count || 0));
        // iter98.99au — Effective lab charge respects discount_type.
        const labCharge = (t) => {
            const raw = Number(t?.lab_charge || 0);
            const d = Number(t?.self_plan_discount || 0);
            const dt = (t?.self_plan_discount_type || "flat").toLowerCase();
            return dt === "percent"
                ? Math.max(0, raw * (1 - d / 100))
                : Math.max(0, raw - d);
        };
        // Global minimum across all active tiers — used for the "below minimum" error.
        const mins = activeTiers.map((t) => Number(t.aligner_min) || 0).filter((n) => n > 0);
        const minRequired = mins.length > 0 ? Math.min(...mins) : 0;
        if (minRequired > 0 && totalStages < minRequired) {
            return {
                kind: "below-min",
                minRequired,
                shortBy: minRequired - totalStages,
            };
        }
        // Match the first tier where aligner_min ≤ stages ≤ aligner_count.
        const fitting = sortedAsc.find((t) => {
            const mn = Number(t.aligner_min) || 0;
            const mx = Number(t.aligner_count) || 0;
            return totalStages >= mn && totalStages <= mx;
        });
        const largest = sortedAsc[sortedAsc.length - 1];
        const largestCap = Number(largest?.aligner_count || 0);
        if (!fitting) {
            return {
                kind: "exceeds-all",
                cap: largestCap,
                name: largest?.name,
                shortBy: totalStages - largestCap,
            };
        }
        const result = {
            tier: fitting,
            price: labCharge(fitting),
            // iter98.99aw — Surface the range + what the doctor GETS for
            // the price (refinement allowance, retainer set) so the
            // inline pill in the Wear Schedule form reads as actionable
            // commercial info rather than restating clinical signals.
            range: `${Number(fitting.aligner_min) || 0}-${Number(fitting.aligner_count) || 0}`,
            refinementCopy: (() => {
                if (!fitting.refinement_included) return null;
                const n = Number(fitting.refinement_count) || 1;
                const cap = Number(fitting.refinement_aligner_count) || 0;
                if (cap > 0) return `${n}× refinement (up to ${cap} stages)`;
                return `${n}× refinement`;
            })(),
            retainerCopy: (() => {
                if (!fitting.retainers_included) return null;
                const n = Number(fitting.included_retainer_count) || 1;
                return `${n} retainer set${n === 1 ? "" : "s"}`;
            })(),
        };
        if (fitting === sortedAsc[0]) {
            return { kind: "fits-all", ...result };
        }
        return {
            kind: "needs-upgrade",
            ...result,
            smallestCap: Number(sortedAsc[0].aligner_count || 0),
            smallestName: sortedAsc[0].name,
        };
    }, [tiers, totalStages]);

    /* iter97.76 — Enhancement #5: Template library. Saves the doctor's
       most-used Lab Instruction bundles to localStorage so they can
       one-tap apply a "standard" protocol across cases. Storage is
       per-browser; no backend changes needed. */
    const TEMPLATE_KEY = "invisique:lab-instruction-templates:v1";
    const [templates, setTemplates] = useState(() => {
        try {
            const raw = localStorage.getItem(TEMPLATE_KEY);
            return raw ? (JSON.parse(raw) || []) : [];
        } catch (e) {
            return [];
        }
    });
    const [showSaveDialog, setShowSaveDialog] = useState(false);
    const [templateNameInput, setTemplateNameInput] = useState("");
    const persistTemplates = (next) => {
        setTemplates(next);
        try { localStorage.setItem(TEMPLATE_KEY, JSON.stringify(next)); } catch (e) { /* ignore */ }
    };
    const captureTemplateData = () => ({
        arch: archValue,
        wear_schedule_default_days: wearDays,
        ipr_directive: iprDir,
        attachments_directive: attDir,
        elastics_directive: elaDir,
        patient_flags: flags,
        case_complexity: complexity,
        shipment_preference: shipPref,
        shipment_notes: rx?.shipment_notes || "",
        do_not_touch_teeth: rx?.do_not_touch_teeth || "",
        lab_notes: rx?.lab_notes || "",
    });
    const saveTemplate = () => {
        const name = templateNameInput.trim();
        if (!name) return;
        const exists = templates.findIndex((t) => t.name === name);
        const data = captureTemplateData();
        const newEntry = { name, data, saved_at: new Date().toISOString() };
        const next = exists >= 0 ? templates.map((t, i) => i === exists ? newEntry : t) : [...templates, newEntry];
        persistTemplates(next);
        setTemplateNameInput("");
        setShowSaveDialog(false);
        toast.success(`Template "${name}" saved`);
    };
    const applyTemplate = (tpl) => {
        const d = tpl.data || {};
        if (d.arch) updClinical("arch", d.arch);
        if (d.wear_schedule_default_days != null) updPlan("wear_schedule_default_days", d.wear_schedule_default_days);
        const rxPatch = {};
        ["ipr_directive", "attachments_directive", "elastics_directive", "patient_flags",
         "case_complexity", "shipment_preference", "shipment_notes",
         "do_not_touch_teeth", "lab_notes"].forEach((k) => {
            if (d[k] !== undefined) rxPatch[k] = d[k];
        });
        if (Object.keys(rxPatch).length > 0) {
            setRx((p) => ({ ...p, ...rxPatch }));
        }
        toast.success(`Applied "${tpl.name}"`);
    };
    const deleteTemplate = (name) => {
        persistTemplates(templates.filter((t) => t.name !== name));
        toast.success(`Template "${name}" deleted`);
    };

    /* iter97.76 — Enhancement #6: PDF preview via print. Renders a
       printable view of the lab order in a new window using
       window.print(). Keeps everything client-side, no backend trip. */
    const printLabOrder = () => {
        const w = window.open("", "_blank", "width=820,height=900");
        if (!w) {
            toast.error("Pop-up blocked. Allow pop-ups to preview the lab order.");
            return;
        }
        const rows = [
            ["Arches", archLabelLocal()],
            /* iter98.99ak — Aligner counts / wear schedule / ETA rows
               are Self-Plan only. CoE leaves these blank — the
               Invisique planner sets them after submission. */
            ...(isSelfPlan ? [
                ["Stages", `${totalStages} stages · ${physicalAligners} physical aligners`],
                ["Wear schedule", wearLabelLocal()],
                ["Estimated treatment", etaWeeks != null ? `≈ ${etaWeeks} weeks (${etaMonths} months)` : "—"],
            ] : []),
            ["IPR", directiveLabel(iprDir)],
            ["Attachments", directiveLabel(attDir)],
            ["Elastics / precision cuts", directiveLabel(elaDir)],
            ["Patient flags", flagsLabelLocal()],
            ["Complexity", complexityLabel],
            ["Shipment", shipmentLabel],
            ["Do not move", rx?.do_not_touch_teeth || "—"],
            ["Lab notes", rx?.lab_notes || "—"],
        ];
        const tableRows = rows.map(([k, v]) =>
            `<tr><th style="text-align:left;padding:8px 12px;background:#f7faf9;border-bottom:1px solid #e3edea;font-size:12px;color:#5a6e6a;font-weight:600;width:38%;">${k}</th><td style="padding:8px 12px;border-bottom:1px solid #e3edea;font-size:13px;color:#0f2722;white-space:pre-wrap;">${String(v).replace(/</g, "&lt;")}</td></tr>`
        ).join("");
        w.document.write(`
            <html><head><title>Invisique™ — Lab Order Preview</title>
            <style>
                body{font-family:-apple-system,BlinkMacSystemFont,'Segoe UI',Roboto,sans-serif;color:#0f2722;padding:32px;max-width:760px;margin:0 auto;}
                h1{font-size:20px;margin:0 0 4px;letter-spacing:-0.01em;}
                .eyebrow{font-size:10px;font-weight:700;letter-spacing:0.18em;text-transform:uppercase;color:#0d8a7c;margin-bottom:6px;}
                .sub{font-size:12px;color:#5a6e6a;margin-bottom:24px;}
                table{width:100%;border-collapse:collapse;border:1px solid #e3edea;border-radius:8px;overflow:hidden;}
                .footer{margin-top:24px;font-size:11px;color:#94a3a0;border-top:1px solid #e3edea;padding-top:12px;}
                @media print{ body{padding:0;} @page{ size:A4; margin:14mm; @top-left{content:"";} @top-center{content:"";} @top-right{content:"";} @bottom-left{content:"";} @bottom-center{content:"";} @bottom-right{content:"";} } }
            </style></head><body>
            <div class="eyebrow">Invisique™ · ${isSelfPlan ? "Self-Plan" : "CoE Planning"}</div>
            <h1>Lab order preview</h1>
            <div class="sub">Draft preview — not yet sent to the lab. Submit the case to dispatch this order.</div>
            <table>${tableRows}</table>
            <div class="footer">Generated ${new Date().toLocaleString()}</div>
            <script>setTimeout(() => window.print(), 250);</script>
            </body></html>
        `);
        w.document.close();
    };

    // ─── Summary helpers (for "Lab order summary" glance box) ───
    const archLabel = archValue === "upper" ? "Upper only" : archValue === "lower" ? "Lower only" : "Upper + Lower";
    const archCountLabel =
        archValue === "upper" ? `${upperCount || 0} U`
        : archValue === "lower" ? `${lowerCount || 0} L`
        : `${upperCount || 0} U / ${lowerCount || 0} L`;
    const wearLabel = wearDays != null
        ? `${wearDays} days / stage${wearIsCustom ? " (custom)" : ""}`
        : "—";
    const directiveLabel = (v) => v === "none" ? "Skip" : v === "modified" ? "Modified" : "Per plan";
    const complexityLabel = complexity === "mild" ? "Mild" : complexity === "complex" ? "Complex" : "Moderate";
    const shipmentLabel = isPhased ? "Phase shipment" : "All at once";
    // Local refs for print closure — defined once values above settle.
    const archLabelLocal = () => archLabel;
    const archCountLabelLocal = () => archCountLabel;
    const wearLabelLocal = () => wearLabel;
    const flagsLabelLocal = () => flagsLabel;

    /* iter97.76 — Enhancement #10: Per-section reset handlers. Each
       resets only that section's state to defaults. */
    const resetArches = () => {
        updClinical("arch", "both");
        updPlan("aligner_count_upper", "");
        updPlan("aligner_count_lower", "");
        updPlan("wear_schedule_default_days", null);
        toast.success("Arches & aligners reset");
    };
    const resetPrefs = () => {
        setRx((p) => ({
            ...p,
            ipr_directive: "per_plan",
            attachments_directive: "per_plan",
            elastics_directive: "per_plan",
        }));
        toast.success("Clinical preferences reset");
    };
    const resetProfile = () => {
        setRx((p) => ({ ...p, patient_flags: [], case_complexity: "moderate", custom_thickness_mm: "" }));
        toast.success("Aligner material considerations reset");
    };
    const resetShipment = () => {
        setRx((p) => ({
            ...p,
            shipment_preference: "all_at_once",
            shipment_notes: "",
            shipment_phase_split: null,
        }));
        toast.success("Shipment preference reset");
    };
    const resetDoNotMove = () => {
        upd("do_not_touch_teeth", "");
        toast.success("Do-not-move list cleared");
    };
    const resetNotes = () => {
        upd("lab_notes", "");
        toast.success("Lab notes cleared");
    };
    const flagsLabel =
        hasBrux && hasTmj ? "Bruxism + TMJ"
        : hasBrux ? "Bruxism"
        : hasTmj ? "TMJ sensitivity"
        : hasNone ? "None reported"
        : "Not specified";

    return (
        <div className="space-y-3 pb-[40vh]" data-testid="case-wizard-lab-instructions" data-mode={isSelfPlan ? "self-plan" : "coe"}>
            {/* iter98.99w — Section-level progress bar removed at
                user direction. The accordion summaries on each
                section card already give doctors enough at-a-glance
                signal of completeness without an extra horizontal
                progress meter. */}

            {/* iter98.99u — Intro line removed per user direction —
                the section headers + accordion cards already make
                "instructions to the lab" obvious without preamble. */}

            {/* iter98.99r — Templates bar removed at user direction.
                The localStorage-backed protocol-templates feature was
                noisy and rarely used; doctors can re-author their
                preferences per case faster than picking from saved
                templates. The state hooks below remain only so a
                future re-introduction doesn't need to rewire the
                effects; they're inert with no UI. */}

            {/* ── 1. Arches & stages ─────────────────────── */}
            <LabSection
                icon={<Tooth size={18} weight="duotone" />}
                tone="teal"
                title={isSelfPlan ? "Arches & Stages" : "Arches in Treatment"}
                desc={isSelfPlan
                    ? "Confirm which arches are in treatment and the per-arch piece count. 1 stage = 1 upper + 1 lower aligner."
                    : "Confirm which arches are in treatment. Your Invisique™ planner will set the stage count + wear cadence on their side."}
                testid="lab-section-arches"
                onReset={resetArches}
                summary={
                    isSelfPlan
                        ? (totalStages > 0 ? `${archLabel} · ${totalStages} stages` : null)
                        : (archValue ? archLabel : null)
                }
            >
                <div className="text-[11px] font-bold tracking-[0.12em] uppercase text-muted-foreground mb-2.5">
                    Arches in treatment
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-3 gap-2.5 mb-5">
                    <LabChoiceCard
                        icon={<ArrowSquareUp size={18} weight="duotone" />}
                        label="Upper only"
                        sub="Manufacture only the maxillary arch"
                        selected={archValue === "upper"}
                        onClick={() => updClinical("arch", "upper")}
                        testid="lab-arch-upper"
                        accent="teal"
                    />
                    <LabChoiceCard
                        icon={<ArrowSquareDown size={18} weight="duotone" />}
                        label="Lower only"
                        sub="Manufacture only the mandibular arch"
                        selected={archValue === "lower"}
                        onClick={() => updClinical("arch", "lower")}
                        testid="lab-arch-lower"
                        accent="teal"
                    />
                    <LabChoiceCard
                        icon={<Tooth size={18} weight="duotone" />}
                        label="Both arches"
                        sub="Full-mouth treatment — most common"
                        selected={archValue === "both"}
                        onClick={() => updClinical("arch", "both")}
                        testid="lab-arch-both"
                        accent="teal"
                    />
                </div>

                {/* iter98.99ak / iter98.99dc — Per-arch STAGE counts +
                    tier preview + wear schedule. Self-Plan only.
                    The fields capture pieces-per-arch; total treatment
                    stages = max(upper, lower) (shown as a derived chip
                    below). For CoE cases, the Invisique planner authors
                    these post-submission, so the whole block hides. */}
                {isSelfPlan && (
                <>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mb-2">
                    <div className="rounded-xl border-2 border-border bg-card p-3 transition-colors hover:border-primary/40 focus-within:border-primary focus-within:bg-primary/[0.02]">
                        <Label className="text-[10.5px] font-bold tracking-[0.1em] uppercase text-muted-foreground flex items-center gap-1.5">
                            <ArrowSquareUp size={12} weight="bold" /> Upper stages
                        </Label>
                        <div className="mt-1.5 flex items-baseline gap-2">
                            <Input
                                type="number" min="0" max="60" step="1"
                                value={upperCount}
                                onChange={(e) => updPlan("aligner_count_upper", e.target.value)}
                                placeholder="0"
                                className="h-11 border-0 focus-visible:ring-0 text-[22px] font-bold p-0 bg-transparent w-full"
                                disabled={archValue === "lower"}
                                data-testid="case-wizard-aligner-count-upper"
                            />
                            <span className="text-[11px] text-muted-foreground whitespace-nowrap">stages</span>
                        </div>
                    </div>
                    <div className="rounded-xl border-2 border-border bg-card p-3 transition-colors hover:border-primary/40 focus-within:border-primary focus-within:bg-primary/[0.02]">
                        <Label className="text-[10.5px] font-bold tracking-[0.1em] uppercase text-muted-foreground flex items-center gap-1.5">
                            <ArrowSquareDown size={12} weight="bold" /> Lower stages
                        </Label>
                        <div className="mt-1.5 flex items-baseline gap-2">
                            <Input
                                type="number" min="0" max="60" step="1"
                                value={lowerCount}
                                onChange={(e) => updPlan("aligner_count_lower", e.target.value)}
                                placeholder="0"
                                className="h-11 border-0 focus-visible:ring-0 text-[22px] font-bold p-0 bg-transparent w-full"
                                disabled={archValue === "upper"}
                                data-testid="case-wizard-aligner-count-lower"
                            />
                            <span className="text-[11px] text-muted-foreground whitespace-nowrap">stages</span>
                        </div>
                    </div>
                </div>

                {/* iter98.99dc — Derived total-stages chip so the doctor
                    immediately sees how the two per-arch counts collapse
                    into the canonical app-wide stage count = max(U, L).
                    Also surfaces the physical-aligner total so they
                    understand what ships. */}
                {totalStages > 0 && (
                    <div
                        className="mb-4 inline-flex items-center flex-wrap gap-x-2 gap-y-1 text-[11px] font-semibold px-2.5 py-1.5 rounded-full bg-teal-50 text-teal-800 border-[0.5px] border-teal-300"
                        data-testid="lab-total-stages-pill"
                    >
                        <span className="inline-flex items-center gap-1.5">
                            <Calculator size={11} weight="fill" />
                            Treatment depth:&nbsp;
                            <span className="font-bold tabular-nums" data-testid="lab-total-stages">{totalStages}</span>&nbsp;stages
                        </span>
                        <span className="text-teal-700/80 font-normal">·&nbsp;1 upper + 1 lower per stage</span>
                        <span className="text-teal-700/80 font-normal">·&nbsp;~<span className="font-semibold tabular-nums">{physicalAligners}</span> physical aligners</span>
                    </div>
                )}

                {/* iter98.99au — Inline tier preview pill in the Wear
                    Schedule form. Surfaces tier name, range, complexity,
                    duration, price the moment Upper + Lower aligner
                    counts produce a positive total — mirrors the server-
                    side `autoSelfPlanTier` rule (aligner_min ≤ total ≤
                    aligner_count) so doctors get an instant "what tier
                    did I just earn?" answer without waiting for Step 6.
                    New states: 'below-min' (you typed too few) +
                    'exceeds-all' (existing). */}
                {tierFit && (
                    <div className="-mt-2 mb-4" data-testid="lab-tier-preview-row">
                        {tierFit.kind === "below-min" ? (
                            <span
                                className="inline-flex items-start gap-1.5 text-[11px] font-semibold px-2.5 py-1.5 rounded-full bg-amber-50 text-amber-800 border-[0.5px] border-amber-300"
                                data-testid="lab-tier-preview-below-min"
                            >
                                <Warning size={11} weight="fill" className="mt-[2px]" />
                                Below minimum — add {tierFit.shortBy} more stage{tierFit.shortBy === 1 ? "" : "s"} to reach the smallest tier ({tierFit.minRequired}+).
                            </span>
                        ) : tierFit.kind === "exceeds-all" ? (
                            <span
                                className="inline-flex items-start gap-1.5 text-[11px] font-semibold px-2.5 py-1.5 rounded-full bg-rose-50 text-rose-800 border-[0.5px] border-rose-300"
                                data-testid="lab-tier-preview-overflow"
                            >
                                <Warning size={11} weight="fill" className="mt-[2px]" />
                                Plan exceeds max — reduce by {tierFit.shortBy} stage{tierFit.shortBy === 1 ? "" : "s"} to fit {tierFit.name} (cap {tierFit.cap}).
                            </span>
                        ) : (
                            <div
                                className="inline-flex items-center flex-wrap gap-x-2 gap-y-1 text-[11px] font-semibold px-2.5 py-1.5 rounded-full bg-emerald-50 text-emerald-800 border-[0.5px] border-emerald-300"
                                data-testid="lab-tier-preview-pill"
                            >
                                <span className="inline-flex items-center gap-1.5">
                                    <ChartBar size={11} weight="fill" />
                                    Auto-assigned:&nbsp;
                                    <span data-testid="lab-tier-preview-tier-name">{tierFit.tier.name}</span>
                                </span>
                                <span className="text-emerald-700/80 font-normal">{tierFit.range} stages</span>
                                {tierFit.refinementCopy && (
                                    <span className="text-emerald-700/80 font-normal" data-testid="lab-tier-preview-refinement">·&nbsp;{tierFit.refinementCopy}</span>
                                )}
                                {tierFit.retainerCopy && (
                                    <span className="text-emerald-700/80 font-normal" data-testid="lab-tier-preview-retainer">·&nbsp;{tierFit.retainerCopy}</span>
                                )}
                                <span className="text-emerald-700/80 font-normal">·&nbsp;
                                    <span data-testid="lab-tier-preview-price">₹{tierFit.price.toLocaleString("en-IN", { maximumFractionDigits: 0 })}</span>
                                </span>
                            </div>
                        )}
                    </div>
                )}

                <div className="border-t-[0.5px] border-border/60 pt-4">
                    <div className="flex items-baseline justify-between gap-3 mb-3 flex-wrap">
                        <div className="text-[11px] font-bold tracking-[0.12em] uppercase text-muted-foreground">
                            Wear schedule
                        </div>
                        {/* iter97.76 — Live ETA pill (Enhancement #2).
                            Computed from max(upper, lower) × wear days.
                            Surfaces "≈ X weeks (Y months)" as soon as
                            both counts and wear cadence are set. */}
                        {etaWeeks != null && (
                            <span
                                className="inline-flex items-center gap-1.5 text-[10.5px] font-semibold px-2.5 py-1 rounded-full bg-primary/10 text-primary border-[0.5px] border-primary/30"
                                data-testid="lab-wear-eta-pill"
                            >
                                <Hourglass size={11} weight="fill" />
                                ≈ {etaWeeks} weeks · {etaMonths} months
                            </span>
                        )}
                    </div>
                    <div className="grid grid-cols-2 sm:grid-cols-5 gap-2">
                        {PRESET_WEAR.map((d) => (
                            <LabChoiceCard
                                key={d}
                                icon={<Hourglass size={16} weight="duotone" />}
                                label={`${d} days`}
                                sub={d === 7 ? "Aggressive · weekly change" : d === 10 ? "Standard · 10-day cadence" : d === 14 ? "Conservative · slow & gentle" : d === 21 ? "Extended · 3-week cadence" : ""}
                                suffix={d === 10 ? "(most common)" : null}
                                selected={wearDays === d}
                                onClick={() => updPlan("wear_schedule_default_days", d)}
                                testid={`lab-wear-${d}`}
                                accent="amber"
                            />
                        ))}
                        <LabChoiceCard
                            icon={<Pencil size={16} weight="duotone" />}
                            label="Custom"
                            sub="Set any cadence you prefer"
                            selected={wearIsCustom}
                            onClick={() => updPlan("wear_schedule_default_days", wearIsCustom ? wearDays : 12)}
                            testid="lab-wear-custom"
                            accent="amber"
                        />
                    </div>

                    {wearIsCustom && (
                        <div className="mt-3">
                            <Label className="text-[11px] text-muted-foreground">Days per stage</Label>
                            <div className="mt-1 flex items-center rounded-[8px] border-[0.5px] border-border overflow-hidden bg-card focus-within:border-primary max-w-[200px]">
                                <Input
                                    type="number" min="1" max="90" step="1"
                                    value={wearDays || ""}
                                    onChange={(e) => updPlan("wear_schedule_default_days", e.target.value)}
                                    placeholder="e.g. 12"
                                    className="h-9 border-0 focus-visible:ring-0 text-sm flex-1"
                                    data-testid="case-wizard-wear-custom"
                                />
                                <span className="text-[11px] text-muted-foreground px-2.5 border-l-[0.5px] border-border/60 whitespace-nowrap">days / stage</span>
                            </div>
                            <LabInfoNote tone="violet">
                                Custom schedule will be applied uniformly across all stages. You can adjust per-stage overrides after the case is created.
                            </LabInfoNote>
                        </div>
                    )}
                </div>
                </>
                )}
                {/* iter98.99ak — CoE-only callout closing the planner-authored
                    fields. Lets the doctor know what they're skipping
                    intentionally rather than thinking they missed a step. */}
                {!isSelfPlan && (
                    <LabInfoNote tone="violet">
                        Stage count, wear schedule, and treatment timeline are authored by your Invisique™ planner after they review your imaging and clinical notes. You&apos;ll get a chance to approve the full plan before manufacturing begins.
                    </LabInfoNote>
                )}
            </LabSection>

            <LabSection
                icon={<UserIcon size={18} weight="duotone" />}
                tone="rose"
                title="Aligner Material Considerations"
                desc="Clinical flags that drive material and gauge selection for this case."
                testid="lab-section-profile"
                onReset={resetProfile}
                summary={
                    flagsLabel !== "Not specified" || complexity !== "moderate"
                        ? `${flagsLabel !== "Not specified" ? flagsLabel : "Standard"} · ${complexityLabel}`
                        : null
                }
            >
                {/* iter98.99x — Material defaults summary. Shows the
                    auto-selected aligner material, the custom-thickness
                    hint field, and the auto-populated retainer material
                    in a single 3-up grid so doctors instantly see what
                    the lab will fabricate against. Material + retainer
                    are read-only (auto-populated); thickness is the only
                    field the doctor edits.
                */}
                {(() => {
                    const raw = String(rx?.custom_thickness_mm || "").trim();
                    const n = raw ? parseFloat(raw) : null;
                    const outOfRange = n != null && Number.isFinite(n) && (n < 0.4 || n > 1.0);
                    return (
                        <div className="mb-4" data-testid="lab-material-defaults">
                            <div className="text-[11px] font-bold tracking-[0.12em] uppercase text-muted-foreground mb-1.5">
                                Material &amp; Gauge defaults
                            </div>
                            <p className="text-[11px] text-muted-foreground mb-3 leading-snug">
                                Pre-selected per Invisique™&apos;s standard configuration. Override thickness only if your clinical plan demands it.
                            </p>
                            <div className="grid grid-cols-1 sm:grid-cols-3 gap-2.5">
                                {/* Aligner Material */}
                                <div
                                    data-testid="lab-aligner-material"
                                    className="relative rounded-xl border border-border bg-card p-3"
                                >
                                    <div className="flex items-start gap-2.5">
                                        <div className="shrink-0 w-10 h-10 rounded-lg grid place-items-center bg-primary/10 text-primary">
                                            <Cube size={16} weight="duotone" />
                                        </div>
                                        <div className="flex-1 min-w-0">
                                            <div className="text-[10px] font-bold tracking-[0.12em] uppercase text-muted-foreground">
                                                Aligner Material
                                            </div>
                                            <div className="text-[13px] font-semibold text-foreground mt-0.5 leading-tight">
                                                Standard Material
                                            </div>
                                            <div className="text-[10.5px] text-muted-foreground mt-1 inline-flex items-center gap-1">
                                                <Check size={10} weight="bold" className="text-primary" /> Auto-selected
                                            </div>
                                        </div>
                                    </div>
                                </div>

                                {/* Aligner Thickness (editable) */}
                                <div
                                    data-testid="lab-aligner-thickness-card"
                                    className={`relative rounded-xl border ${outOfRange ? "border-amber-400" : "border-border"} bg-card p-3`}
                                >
                                    <div className="flex items-start gap-2.5">
                                        <div className="shrink-0 w-10 h-10 rounded-lg grid place-items-center bg-amber-100 text-amber-700">
                                            <Ruler size={16} weight="duotone" />
                                        </div>
                                        <div className="flex-1 min-w-0">
                                            <Label
                                                htmlFor="lab-custom-thickness"
                                                className="text-[10px] font-bold tracking-[0.12em] uppercase text-muted-foreground"
                                            >
                                                Aligner Thickness (mm)
                                            </Label>
                                            <Input
                                                id="lab-custom-thickness"
                                                type="number"
                                                inputMode="decimal"
                                                step="0.05"
                                                min="0.4"
                                                max="1.0"
                                                placeholder="Standard"
                                                value={rx?.custom_thickness_mm || ""}
                                                onChange={(e) => upd("custom_thickness_mm", e.target.value)}
                                                className={`mt-1 h-8 text-[13px] ${outOfRange ? "border-amber-400 focus-visible:ring-amber-300" : ""}`}
                                                data-testid="lab-custom-thickness"
                                                aria-invalid={outOfRange || undefined}
                                            />
                                            <p className={`text-[10.5px] mt-1 leading-snug ${outOfRange ? "text-amber-700" : "text-muted-foreground"}`}>
                                                {outOfRange
                                                    ? "Out of range (0.4 – 1.0 mm) · QA will flag at submission"
                                                    : "Optional · range 0.4 – 1.0 mm"}
                                            </p>
                                        </div>
                                    </div>
                                </div>

                                {/* Retainer Material */}
                                <div
                                    data-testid="lab-retainer-material"
                                    className="relative rounded-xl border border-border bg-card p-3"
                                >
                                    <div className="flex items-start gap-2.5">
                                        <div className="shrink-0 w-10 h-10 rounded-lg grid place-items-center bg-violet-100 text-violet-700">
                                            <Package size={16} weight="duotone" />
                                        </div>
                                        <div className="flex-1 min-w-0">
                                            <div className="text-[10px] font-bold tracking-[0.12em] uppercase text-muted-foreground">
                                                Retainer Material
                                            </div>
                                            <div className="text-[13px] font-semibold text-foreground mt-0.5 leading-tight">
                                                Invisique™&apos;s Infinite
                                            </div>
                                            <div className="text-[10.5px] text-muted-foreground mt-1 inline-flex items-center gap-1">
                                                <Check size={10} weight="bold" className="text-violet-600" /> Auto-populated
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    );
                })()}

                <div className="pt-4 mt-1 border-t-[0.5px] border-border/60">
                <div className="text-[11px] font-bold tracking-[0.12em] uppercase text-muted-foreground mb-2">
                    Does this patient have any of the following?
                </div>
                <div className="space-y-0">
                    <label className="flex items-start gap-2.5 py-2 border-b-[0.5px] border-border/50 cursor-pointer" data-testid="lab-flag-bruxism">
                        <input
                            type="checkbox"
                            checked={hasBrux}
                            onChange={() => toggleFlag("bruxism")}
                            className="mt-0.5 w-4 h-4 accent-primary cursor-pointer"
                        />
                        <div>
                            <div className="text-[13px] font-medium text-foreground">Bruxism (teeth grinding)</div>
                            <div className="text-[11px] text-muted-foreground mt-0.5">We&apos;ll select a more durable, impact-resistant material.</div>
                        </div>
                    </label>
                    <label className="flex items-start gap-2.5 py-2 border-b-[0.5px] border-border/50 cursor-pointer" data-testid="lab-flag-tmj">
                        <input
                            type="checkbox"
                            checked={hasTmj}
                            onChange={() => toggleFlag("tmj")}
                            className="mt-0.5 w-4 h-4 accent-primary cursor-pointer"
                        />
                        <div>
                            <div className="text-[13px] font-medium text-foreground">TMJ sensitivity</div>
                            <div className="text-[11px] text-muted-foreground mt-0.5">We&apos;ll select a softer, lower-force material to reduce joint stress.</div>
                        </div>
                    </label>
                    <label className="flex items-start gap-2.5 py-2 cursor-pointer" data-testid="lab-flag-none">
                        <input
                            type="checkbox"
                            checked={hasNone}
                            onChange={() => toggleFlag("none")}
                            className="mt-0.5 w-4 h-4 accent-primary cursor-pointer"
                        />
                        <div>
                            <div className="text-[13px] font-medium text-foreground">None of the above</div>
                            <div className="text-[11px] text-muted-foreground mt-0.5">Standard material will be used.</div>
                        </div>
                    </label>
                </div>

                {/* iter98.99s — Case complexity redesigned. Was a flat
                    "Mild / Moderate / Complex" chip row; now a 3-up
                    card grid with per-tier guidance ("thinner sheet" /
                    "balanced" / "firmer sheet") and an active-card
                    visual treatment. */}
                <div className="pt-4 mt-3 border-t-[0.5px] border-border/60">
                    <div className="text-[11px] font-bold tracking-[0.12em] uppercase text-muted-foreground mb-2">
                        Case complexity
                    </div>
                    <p className="text-[11px] text-muted-foreground mb-2.5 leading-snug">
                        Drives the aligner sheet gauge — thinner for mild rotation, firmer for complex movements.
                    </p>
                    <div className="grid grid-cols-1 sm:grid-cols-3 gap-2.5">
                        <LabChoiceCard
                            icon={<Circle size={16} weight="regular" />}
                            label="Mild"
                            sub="Minor crowding or relapse · thinner flexible sheet"
                            selected={complexity === "mild"}
                            onClick={() => upd("case_complexity", "mild")}
                            testid="lab-cx-mild"
                            accent="teal"
                        />
                        <LabChoiceCard
                            icon={<CircleHalf size={16} weight="fill" />}
                            label="Moderate"
                            sub="Most common · balanced sheet for general movement"
                            selected={complexity === "moderate"}
                            onClick={() => upd("case_complexity", "moderate")}
                            testid="lab-cx-moderate"
                            accent="amber"
                            suffix="(most common)"
                        />
                        <LabChoiceCard
                            icon={<Circle size={16} weight="fill" />}
                            label="Complex"
                            sub="Severe rotations or extrusions · firmer sheet"
                            selected={complexity === "complex"}
                            onClick={() => upd("case_complexity", "complex")}
                            testid="lab-cx-complex"
                            accent="violet"
                        />
                    </div>
                </div>

                {/* iter98.99ak — Custom thickness moved into the
                    Material & Gauge defaults grid at the top of this
                    section (iter98.99x). */}
                </div>
            </LabSection>

            {/* ── 3. Clinical preferences ─────────────────────────── */}
            <LabSection
                icon={<Sliders size={18} weight="duotone" />}
                tone="indigo"
                title="Clinical Preferences"
                desc="Tell the lab how to handle IPR, attachments, and elastics for this case."
                testid="lab-section-prefs"
                onReset={resetPrefs}
                summary={`IPR ${directiveLabel(iprDir)} · Att ${directiveLabel(attDir)} · Elastics ${directiveLabel(elaDir)}`}
            >
                {/* IPR */}
                <div className="mb-4">
                    <Label className="text-[11px] text-muted-foreground mb-2 block">IPR</Label>
                    <div>
                        <LabChip icon={<Check size={12} weight="bold" />} label="Per plan" selected={iprDir === "per_plan"} onClick={() => upd("ipr_directive", "per_plan")} testid="lab-ipr-per-plan" />
                        <LabChip icon={<X size={12} weight="bold" />} label="No IPR" selected={iprDir === "none"} onClick={() => upd("ipr_directive", "none")} testid="lab-ipr-none" />
                        <LabChip icon={<Pencil size={12} weight="bold" />} label="Modified — see notes" selected={iprDir === "modified"} onClick={() => upd("ipr_directive", "modified")} testid="lab-ipr-modified" />
                    </div>
                    {/* iter98.99s — Inline notes appear when "Modified" is
                        picked so the doctor can capture the IPR-specific
                        modifications right here (no jumping to a global
                        notes box). */}
                    {iprDir === "modified" && (
                        <div className="mt-2.5">
                            <Label className="text-[10.5px] font-bold tracking-[0.1em] uppercase text-muted-foreground">
                                IPR modification notes
                            </Label>
                            <Textarea
                                rows={2}
                                value={rx?.ipr_modified_notes || ""}
                                onChange={(e) => upd("ipr_modified_notes", e.target.value)}
                                placeholder="e.g. Skip IPR at 13-14 contact, increase IPR to 0.5mm at 25-26…"
                                className="mt-1 text-sm"
                                data-testid="case-wizard-ipr-modified-notes"
                            />
                        </div>
                    )}
                </div>
                {/* Attachments */}
                <div className="mb-4 pt-3 border-t-[0.5px] border-border/60">
                    <Label className="text-[11px] text-muted-foreground mb-2 block">Attachments</Label>
                    <div>
                        <LabChip icon={<Check size={12} weight="bold" />} label="Per plan" selected={attDir === "per_plan"} onClick={() => upd("attachments_directive", "per_plan")} testid="lab-att-per-plan" />
                        <LabChip icon={<X size={12} weight="bold" />} label="No attachments" selected={attDir === "none"} onClick={() => upd("attachments_directive", "none")} testid="lab-att-none" />
                        <LabChip icon={<Pencil size={12} weight="bold" />} label="Modified — see notes" selected={attDir === "modified"} onClick={() => upd("attachments_directive", "modified")} testid="lab-att-modified" />
                    </div>
                    {attDir === "modified" && (
                        <div className="mt-2.5">
                            <Label className="text-[10.5px] font-bold tracking-[0.1em] uppercase text-muted-foreground">
                                Attachment modification notes
                            </Label>
                            <Textarea
                                rows={2}
                                value={rx?.attachments_modified_notes || ""}
                                onChange={(e) => upd("attachments_modified_notes", e.target.value)}
                                placeholder="e.g. Replace beveled attachment at 14 with horizontal rectangular, skip attachment at 24…"
                                className="mt-1 text-sm"
                                data-testid="case-wizard-attachments-modified-notes"
                            />
                        </div>
                    )}
                </div>
                {/* Elastics / precision cuts */}
                <div className="pt-3 border-t-[0.5px] border-border/60">
                    <Label className="text-[11px] text-muted-foreground mb-2 block">Elastics / precision cuts</Label>
                    <div>
                        <LabChip icon={<X size={12} weight="bold" />} label="Not required" selected={elaDir === "none"} onClick={() => upd("elastics_directive", "none")} testid="lab-ela-none" />
                        <LabChip icon={<Check size={12} weight="bold" />} label="Include — per plan" selected={elaDir === "per_plan"} onClick={() => upd("elastics_directive", "per_plan")} testid="lab-ela-per-plan" />
                        <LabChip icon={<Pencil size={12} weight="bold" />} label="Modified — see notes" selected={elaDir === "modified"} onClick={() => upd("elastics_directive", "modified")} testid="lab-ela-modified" />
                    </div>
                    {elaDir === "modified" && (
                        <div className="mt-2.5">
                            <Label className="text-[10.5px] font-bold tracking-[0.1em] uppercase text-muted-foreground">
                                Elastics / cuts modification notes
                            </Label>
                            <Textarea
                                rows={2}
                                value={rx?.elastics_modified_notes || ""}
                                onChange={(e) => upd("elastics_modified_notes", e.target.value)}
                                placeholder="e.g. Add class II elastic from 16 to 46, precision cut buccal slot at 17…"
                                className="mt-1 text-sm"
                                data-testid="case-wizard-elastics-modified-notes"
                            />
                        </div>
                    )}
                </div>
            </LabSection>

            {/* ── 4. Do not move ────────────────────────────────── */}
            <LabSection
                icon={<Prohibit size={18} weight="duotone" />}
                tone="red"
                title="Do Not Move"
                desc="Specify any teeth the lab must exclude from movement — implants, bridges, ankylosed teeth, or doctor preference."
                testid="lab-section-do-not-move"
                onReset={resetDoNotMove}
                summary={(rx?.do_not_touch_teeth || "").trim() ? `${(rx.do_not_touch_teeth.match(/\d+/g) || []).length} teeth locked` : null}
            >
                {/* iter97.76 — FDI quick-tap chip picker (Enhancement #3).
                    Sits above the free-text textarea. Clicking a tooth
                    chip appends/removes its FDI number to the textarea
                    below — chips are derived from textarea content so
                    the two stay in sync. */}
                <div className="text-[11px] font-bold tracking-[0.12em] uppercase text-muted-foreground mb-2">
                    Quick-tap teeth (FDI)
                </div>
                <FdiChipPicker
                    value={rx?.do_not_touch_teeth || ""}
                    onChange={(v) => upd("do_not_touch_teeth", v)}
                    testid="lab-fdi-picker"
                />
                <div className="mt-4 pt-3 border-t-[0.5px] border-border/60">
                    <Label className="text-[11px] text-muted-foreground">Teeth list & notes (free-text)</Label>
                    <Textarea
                        rows={3}
                        value={rx?.do_not_touch_teeth || ""}
                        onChange={(e) => upd("do_not_touch_teeth", e.target.value)}
                        placeholder="e.g. 36, 11 — implant crown on 36; ankylosed at 11. Avoid any tipping…"
                        className="mt-1 text-sm"
                        data-testid="case-wizard-do-not-touch-teeth"
                    />
                    <p className="text-[10px] text-muted-foreground mt-1">
                        Tap chips above for quick selection, or type freely here — they stay in sync.
                    </p>
                </div>
            </LabSection>

            {/* ── 5. Custom notes ────────────────────────────────── */}
            <LabSection
                icon={<Notepad size={18} weight="duotone" />}
                tone="cyan"
                title="Custom Notes for the Lab"
                desc="Anything else the lab needs to know. Free-text catch-all for edge cases or special instructions not covered above."
                testid="lab-section-notes"
                onReset={resetNotes}
                summary={(rx?.lab_notes || "").trim() ? `${(rx.lab_notes || "").trim().length} chars` : null}
            >
                <Textarea
                    rows={4}
                    value={rx?.lab_notes || ""}
                    onChange={(e) => upd("lab_notes", e.target.value)}
                    placeholder="e.g. Patient has a fixed lingual retainer on lower anteriors. Please ensure trim line clears the bonding wire…"
                    className="text-sm"
                    data-testid="case-wizard-lab-notes"
                />
            </LabSection>

            {/* ── 6. Shipment preference ─────────────────────────── */}
            <LabSection
                icon={<Package size={18} weight="duotone" />}
                tone="amber"
                title="Shipment Preference"
                desc="Tell the lab how to package and dispatch this order."
                testid="lab-section-shipment"
                onReset={resetShipment}
                summary={shipmentLabel}
            >
                {/* iter98.99s — Shipment preference simplified. The
                    draggable PhaseSplitVisualizer + LabChip pair was
                    replaced with two prominent LabChoiceCard tiles +
                    a clean number input for the phase-1 split when
                    "Phase shipment" is picked. No more drag UX. */}
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-2.5 mb-3">
                    <LabChoiceCard
                        icon={<Package size={18} weight="duotone" />}
                        label="Ship all at once"
                        sub="Single shipment when manufacturing completes — simplest option"
                        selected={!isPhased}
                        onClick={() => upd("shipment_preference", "all_at_once")}
                        testid="lab-ship-all"
                        accent="amber"
                    />
                    <LabChoiceCard
                        icon={<Package size={18} weight="duotone" />}
                        label="Phase shipment"
                        sub="Ship in batches — useful for long treatments or storage limits"
                        selected={isPhased}
                        onClick={() => upd("shipment_preference", "phased")}
                        testid="lab-ship-phased"
                        accent="amber"
                    />
                </div>
                {isPhased && (
                    <div className="bg-amber-50/60 border-[0.5px] border-amber-200 rounded-xl p-3.5 space-y-3" data-testid="lab-ship-phased-config">
                        {isSelfPlan && maxAligners > 1 && (
                            <div>
                                <Label className="text-[11px] font-bold tracking-[0.1em] uppercase text-muted-foreground">
                                    Phase 1 size <span className="font-normal normal-case text-[10.5px] text-muted-foreground/80">— how many stages ship first?</span>
                                </Label>
                                <div className="mt-1.5 flex items-center gap-3">
                                    <div className="flex items-center rounded-[8px] border-2 border-amber-300 bg-card overflow-hidden focus-within:border-amber-500 max-w-[160px]">
                                        <Input
                                            type="number"
                                            min={1}
                                            max={Math.max(1, maxAligners - 1)}
                                            step={1}
                                            value={Number(rx?.shipment_phase_split) || Math.floor(maxAligners / 2)}
                                            onChange={(e) => {
                                                const v = parseInt(e.target.value || "0", 10);
                                                if (!Number.isNaN(v)) upd("shipment_phase_split", Math.max(1, Math.min(maxAligners - 1, v)));
                                            }}
                                            className="h-10 border-0 focus-visible:ring-0 text-[18px] font-bold p-0 px-3 bg-transparent w-full"
                                            data-testid="lab-ship-phase-split-input"
                                        />
                                        <span className="text-[11px] text-muted-foreground px-2.5 border-l border-amber-300 whitespace-nowrap">of {maxAligners}</span>
                                    </div>
                                    <div className="flex-1 text-[11.5px] text-amber-900/85 leading-snug">
                                        Phase 1 ships <span className="font-semibold">{Number(rx?.shipment_phase_split) || Math.floor(maxAligners / 2)} stages</span> · Phase 2 ships <span className="font-semibold">{Math.max(0, maxAligners - (Number(rx?.shipment_phase_split) || Math.floor(maxAligners / 2)))} stages</span>.
                                    </div>
                                </div>
                            </div>
                        )}
                        {!isSelfPlan && (
                            <div className="text-[11px] text-amber-900/80 leading-snug">
                                Your Invisique™ planner will set the exact phase split once the treatment plan is finalised. Capture any preferences below so they can honour them.
                            </div>
                        )}
                        <div>
                            <Label className="text-[11px] text-muted-foreground">Additional phase notes (optional)</Label>
                            <Textarea
                                rows={2}
                                value={rx?.shipment_notes || ""}
                                onChange={(e) => upd("shipment_notes", e.target.value)}
                                placeholder="e.g. Phase 2 ships only after the mid-treatment review and refinement scans are confirmed…"
                                className="mt-1 text-sm"
                                data-testid="case-wizard-shipment-notes"
                            />
                        </div>
                    </div>
                )}
                <LabInfoNote tone="amber">
                    Phase shipments help prevent aligner loss. The lab will package and label each batch separately.
                </LabInfoNote>
            </LabSection>

            {/* ── 7. Lab order summary ────────────────────────────── */}
            <LabSection
                icon={<ListBullets size={18} weight="duotone" />}
                tone="violet"
                title="Lab Order Summary"
                testid="lab-section-summary"
            >
                {/* iter97.76 + iter98.99p + iter98.99dc — Tier fit hint.
                    Uses STAGE count (max(U, L)) — matches the server-side
                    auto-tier rule and the live preview pill above.
                    iter98.99ak — Self-Plan only; CoE picks the tier in
                    Step 1 so this hint is irrelevant for them. */}
                {isSelfPlan && tierFit && tierFit.kind === "exceeds-all" && (
                    <div className="mb-3 flex items-start gap-2 p-2.5 rounded-lg border-[0.5px] bg-rose-50 border-rose-300 text-rose-800" data-testid="lab-tier-fit-exceeds">
                        <Warning size={14} className="shrink-0 mt-0.5" weight="fill" />
                        <p className="text-[11.5px] leading-snug">
                            <span className="font-bold">{totalStages} stages</span> exceeds the largest tier&apos;s cap of <span className="font-bold">{tierFit.cap}</span> ({tierFit.name}).
                            Contact Invisique™ to discuss extended treatment pricing.
                        </p>
                    </div>
                )}
                {isSelfPlan && tierFit && tierFit.kind === "needs-upgrade" && (
                    <div className="mb-3 flex items-start gap-2 p-2.5 rounded-lg border-[0.5px] bg-amber-50 border-amber-300 text-amber-800" data-testid="lab-tier-fit-upgrade">
                        <Info size={14} className="shrink-0 mt-0.5" weight="fill" />
                        <p className="text-[11.5px] leading-snug">
                            <span className="font-bold">{totalStages} stages</span> exceeds the smallest tier ({tierFit.smallestName} · {tierFit.smallestCap} cap).
                            Recommended: <span className="font-bold">{tierFit.tier.name}</span> ({tierFit.tier.aligner_count} stages).
                        </p>
                    </div>
                )}
                {isSelfPlan && tierFit && tierFit.kind === "fits-all" && (
                    <div className="mb-3 flex items-start gap-2 p-2.5 rounded-lg border-[0.5px] bg-emerald-50 border-emerald-300 text-emerald-800" data-testid="lab-tier-fit-ok">
                        <Check size={14} className="shrink-0 mt-0.5" weight="bold" />
                        <p className="text-[11.5px] leading-snug">
                            <span className="font-bold">{totalStages} stages</span> fits every active tier — you&apos;ll get the entry-tier price.
                        </p>
                    </div>
                )}
                <div className="bg-secondary/40 border-[0.5px] border-border/60 rounded-[10px] px-3.5 py-2">
                    <LabGlanceRow k="Arches" v={archLabel} />
                    {/* iter98.99ak — Stages / Wear schedule / Estimated
                        treatment lines are Self-Plan only. For CoE the
                        planner authors all three after submission, so we
                        skip them here to keep the summary honest. */}
                    {isSelfPlan && (
                        <>
                            <LabGlanceRow k="Stages" v={`${archCountLabel}${totalStages > 0 ? ` (${totalStages} stages · ${physicalAligners} physical aligners)` : ""}`} />
                            <LabGlanceRow k="Wear schedule" v={wearLabel} />
                            {etaWeeks != null && (
                                <LabGlanceRow k="Estimated treatment" v={`≈ ${etaWeeks} weeks · ${etaMonths} months`} />
                            )}
                        </>
                    )}
                    <LabGlanceRow k="IPR" v={directiveLabel(iprDir)} />
                    <LabGlanceRow k="Attachments" v={directiveLabel(attDir)} />
                    <LabGlanceRow k="Elastics / precision cuts" v={directiveLabel(elaDir)} />
                    <LabGlanceRow k="Patient flags" v={flagsLabel} />
                    <LabGlanceRow k="Complexity" v={complexityLabel} />
                    <LabGlanceRow k="Shipment" v={shipmentLabel} last />
                </div>
                {/* iter97.76 — PDF preview (Enhancement #6). Opens a
                    printable window showing exactly what the lab will
                    receive when this case is submitted. */}
                <div className="mt-3 flex items-center justify-end">
                    <Button
                        type="button"
                        size="sm"
                        variant="outline"
                        onClick={printLabOrder}
                        className="h-8 text-[11.5px] gap-1.5"
                        data-testid="lab-preview-pdf"
                    >
                        <Printer size={13} weight="bold" />
                        Preview lab order
                    </Button>
                </div>
            </LabSection>
        </div>
    );
}


export function StepLabInstructions({ rx, setRx, metadata, planningMode, planTimeline, setPlanTimeline, clinical, setClinical, selectedTier = null, tiers = [] }) {
    /* iter98.99ak — CoE + Self-Plan now share the same LabSection-based
       accordion shell. `SelfPlanLabInstructions` accepts `planningMode`
       and hides planner-authored sub-blocks (aligner counts, wear
       schedule, treatment timeline, retention recalls, tier-fit hints)
       when CoE. The legacy compact form below has been retired — every
       lab-level decision a doctor needs to capture (Arches, Material,
       IPR/Att/Ela directives, Patient flags, Shipment, Do-not-move,
       Lab notes) now lives in the same component for both flows. */
    void selectedTier;
    void metadata;
    return (
        <SelfPlanLabInstructions
            rx={rx} setRx={setRx}
            planTimeline={planTimeline} setPlanTimeline={setPlanTimeline}
            clinical={clinical} setClinical={setClinical}
            tiers={tiers}
            planningMode={planningMode}
        />
    );
}


/* iter98.99t — Small inclusion-row chip used inside the new Tier &
   Pricing hero card on Review & Submit. Two columns: icon + label
   on the left, value on the right; the whole row gets a soft tinted
   background for the active accent. */
export function TierInclusionRow({ icon, label, value, accent = "emerald" }) {
    const A = {
        emerald: { bg: "bg-emerald-50", icon: "bg-emerald-100 text-emerald-700", label: "text-emerald-900/80", value: "text-emerald-900" },
        violet:  { bg: "bg-violet-50",  icon: "bg-violet-100 text-violet-700",   label: "text-violet-900/80",  value: "text-violet-900" },
        muted:   { bg: "bg-muted/40",   icon: "bg-secondary text-muted-foreground", label: "text-muted-foreground", value: "text-foreground/70 italic" },
    }[accent] || { bg: "bg-muted/40", icon: "bg-secondary text-muted-foreground", label: "text-muted-foreground", value: "text-foreground" };
    return (
        <div className={`flex items-center gap-2.5 rounded-xl px-3 py-2 ${A.bg}`}>
            <div className={`w-7 h-7 rounded-lg grid place-items-center shrink-0 ${A.icon}`}>{icon}</div>
            <div className="min-w-0 flex-1">
                <div className={`text-[10.5px] font-bold tracking-[0.1em] uppercase ${A.label}`}>{label}</div>
                <div className={`text-[12.5px] font-semibold leading-tight mt-0.5 truncate ${A.value}`}>{value}</div>
            </div>
        </div>
    );
}
